import { useQuery } from '@tanstack/react-query';
import {
  Megaphone,
  FileText,
  Share2,
  Calendar,
  Eye,
  MousePointerClick,
  Users,
  CalendarCheck,
  BriefcaseBusiness,
  Trophy,
  AlertCircle,
  Activity,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/Card';
import { StatCard } from '../../components/StatCard';
import { Badge, getStatusVariant } from '../../components/Badge';
import { analyticsApi, campaignsApi, contentApi, socialPostsApi } from '../../lib/api';
import { format } from 'date-fns';
import type { Campaign, Content, SocialPost } from '../../types';

const compactNumber = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });
const currency = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

function checkpointLabel(statuses: string[]) {
  if (statuses.some((status) => status === 'collected')) return 'collected';
  if (statuses.some((status) => status === 'unavailable')) return 'unavailable';
  if (statuses.some((status) => status === 'not_due')) return 'not due';
  return 'awaiting post';
}

export function MarketingOverview() {
  const { data: funnel, isLoading: funnelLoading, error: funnelError } = useQuery({
    queryKey: ['analytics', 'funnel', 'asi_foundations_launch'],
    queryFn: () => analyticsApi.funnel('asi_foundations_launch'),
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => campaignsApi.list({ limit: 100 }),
  });

  const { data: content } = useQuery({
    queryKey: ['content'],
    queryFn: () => contentApi.list({ limit: 100 }),
  });

  const { data: socialPosts } = useQuery({
    queryKey: ['social-posts'],
    queryFn: () => socialPostsApi.list({ limit: 100 }),
  });

  const { data: scheduledPosts } = useQuery({
    queryKey: ['social-posts', 'scheduled'],
    queryFn: () => socialPostsApi.scheduled(),
  });

  const campaignsList = (campaigns?.campaigns || []) as Campaign[];
  const contentList = (content?.content || []) as Content[];
  const socialPostsList = (socialPosts?.posts || []) as SocialPost[];
  const scheduledPostsList = (scheduledPosts?.posts || []) as SocialPost[];

  // Calculate stats
  const activeCampaigns = campaignsList.filter((c) => c.status === 'active');
  const publishedContent = contentList.filter((c) => c.status === 'published');
  const publishedPosts = socialPostsList.filter((p) => p.status === 'published');

  // Platform breakdown
  const postsByPlatform = {
    linkedin: socialPostsList.filter((p) => p.platform === 'linkedin').length,
    facebook: socialPostsList.filter((p) => p.platform === 'facebook').length,
    instagram: socialPostsList.filter((p) => p.platform === 'instagram').length,
    twitter: socialPostsList.filter((p) => p.platform === 'twitter').length,
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Content → Sales Funnel</h1>
          {funnel?.campaign && <Badge variant="info">{funnel.campaign.name}</Badge>}
        </div>
        <p className="text-gray-500 mt-1">Trace each production from platform attention to qualified revenue.</p>
      </div>

      {funnelError && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-none" />
          <div>
            <p className="font-medium">The funnel report could not be loaded.</p>
            <p className="text-sm">{funnelError instanceof Error ? funnelError.message : 'Unknown reporting error'}</p>
          </div>
        </div>
      )}

      {!funnelLoading && funnel && funnel.totals.published_posts === 0 && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-none" />
          <div>
            <p className="font-medium">Measurement is ready and waiting for the pilot.</p>
            <p className="text-sm">All {funnel.totals.draft_posts} platform records are still drafts. Zero platform metrics are not being presented as poor performance.</p>
          </div>
        </div>
      )}

      {funnel && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 xl:grid-cols-8">
            <StatCard title="Impressions" value={compactNumber.format(funnel.totals.impressions)} subtitle="Latest snapshots" icon={<Eye className="h-5 w-5" />} />
            <StatCard title="Engagements" value={compactNumber.format(funnel.totals.engagements)} subtitle="Available platforms" icon={<Share2 className="h-5 w-5" />} />
            <StatCard title="Clicks" value={compactNumber.format(funnel.totals.clicks)} subtitle="Tracked by platform" icon={<MousePointerClick className="h-5 w-5" />} />
            <StatCard title="Site visits" value={funnel.totals.site_visits} subtitle={`${funnel.totals.assessment_starts} diagnostic starts`} icon={<Activity className="h-5 w-5" />} />
            <StatCard title="Leads" value={funnel.totals.leads} subtitle="Exactly attributed" icon={<Users className="h-5 w-5" />} />
            <StatCard title="Booked" value={funnel.totals.bookings} subtitle="Confirmed reviews" icon={<CalendarCheck className="h-5 w-5" />} />
            <StatCard title="Qualified" value={funnel.totals.qualified_deals} subtitle="Deals past discovery" icon={<BriefcaseBusiness className="h-5 w-5" />} />
            <StatCard title="Won" value={funnel.totals.wins} subtitle={currency.format(funnel.totals.won_revenue)} icon={<Trophy className="h-5 w-5" />} variant="dark" />
          </div>

          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Per-video scorecard</CardTitle>
                <p className="mt-1 text-sm text-gray-500">Latest platform snapshot plus explicit 24-hour, 7-day and 30-day collection states.</p>
              </div>
              <Badge variant={funnel.totals.published_posts > 0 ? 'success' : 'warning'}>
                {funnel.totals.published_posts} published / {funnel.totals.draft_posts} draft
              </Badge>
            </CardHeader>
            <CardContent className="overflow-x-auto p-0">
              <table className="min-w-[1050px] w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-5 py-3">Production</th>
                    <th className="px-4 py-3">Platforms</th>
                    <th className="px-4 py-3 text-right">Reach</th>
                    <th className="px-4 py-3 text-right">Engaged</th>
                    <th className="px-4 py-3 text-right">Visits</th>
                    <th className="px-4 py-3 text-right">Leads</th>
                    <th className="px-4 py-3 text-right">Calls</th>
                    <th className="px-4 py-3 text-right">Qualified</th>
                    <th className="px-4 py-3 text-right">Won</th>
                    <th className="px-4 py-3">24h</th>
                    <th className="px-4 py-3">7d</th>
                    <th className="px-4 py-3">30d</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {funnel.content.map((item) => (
                    <tr key={item.id} className="align-top">
                      <td className="px-5 py-4">
                        <p className="font-medium text-gray-900">{item.title}</p>
                        <p className="mt-1 font-mono text-xs text-gray-500">{item.id}</p>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col items-start gap-1.5">
                          {item.posts.map((post) => (
                            <Badge key={post.id} variant={post.status === 'published' ? 'success' : 'default'}>
                              {post.platform} · {post.status}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-right font-medium">{compactNumber.format(item.totals.reach)}</td>
                      <td className="px-4 py-4 text-right font-medium">{compactNumber.format(item.totals.engagements)}</td>
                      <td className="px-4 py-4 text-right font-medium">{item.totals.site_visits}</td>
                      <td className="px-4 py-4 text-right font-medium">{item.totals.leads}</td>
                      <td className="px-4 py-4 text-right font-medium">{item.totals.bookings}</td>
                      <td className="px-4 py-4 text-right font-medium">{item.totals.qualified_deals}</td>
                      <td className="px-4 py-4 text-right font-medium">{item.totals.wins}</td>
                      {(['24h', '7d', '30d'] as const).map((checkpoint) => {
                        const label = checkpointLabel(item.posts.map((post) => post.checkpoints[checkpoint].status));
                        return (
                          <td className="px-4 py-4" key={checkpoint}>
                            <Badge variant={label === 'collected' ? 'success' : label === 'unavailable' ? 'error' : 'default'}>{label}</Badge>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="p-5"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Diagnostic progression</p><p className="mt-2 font-medium text-gray-900">{funnel.totals.assessment_starts} started · {funnel.totals.assessment_completions} completed</p><p className="mt-1 text-sm text-gray-500">First-party, non-PII session events from ASI Demos.</p></Card>
            <Card className="p-5"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Direct messages</p><p className="mt-2 font-medium text-gray-900">Unavailable</p><p className="mt-1 text-sm text-gray-500">No platform inbox ingestion is connected yet.</p></Card>
            <Card className="p-5"><p className="text-xs font-medium uppercase tracking-wide text-gray-500">Revenue chain</p><p className="mt-2 font-medium text-gray-900">Lead → deal → won value</p><p className="mt-1 text-sm text-gray-500">Available whenever an attributed lead becomes a won CRM deal.</p></Card>
          </div>
        </>
      )}

      {/* Operational inventory */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Active Campaigns"
          value={activeCampaigns.length}
          subtitle={`${campaignsList.length} total campaigns`}
          icon={<Megaphone className="w-6 h-6" />}
        />
        <StatCard
          title="Published Content"
          value={publishedContent.length}
          subtitle={`${contentList.length} total pieces`}
          icon={<FileText className="w-6 h-6" />}
        />
        <StatCard
          title="Social Posts"
          value={publishedPosts.length}
          subtitle={`${scheduledPostsList.length} scheduled`}
          icon={<Share2 className="w-6 h-6" />}
        />
        <StatCard
          title="Scheduled"
          value={scheduledPostsList.length}
          subtitle="Ready to publish"
          icon={<Calendar className="w-6 h-6" />}
        />
      </div>

      {/* Platform Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Posts by Platform</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{postsByPlatform.linkedin}</p>
              <p className="text-sm text-gray-600 mt-1">LinkedIn</p>
            </div>
            <div className="text-center p-4 bg-indigo-50 rounded-lg">
              <p className="text-3xl font-bold text-indigo-600">{postsByPlatform.facebook}</p>
              <p className="text-sm text-gray-600 mt-1">Facebook</p>
            </div>
            <div className="text-center p-4 bg-pink-50 rounded-lg">
              <p className="text-3xl font-bold text-pink-600">{postsByPlatform.instagram}</p>
              <p className="text-sm text-gray-600 mt-1">Instagram</p>
            </div>
            <div className="text-center p-4 bg-sky-50 rounded-lg">
              <p className="text-3xl font-bold text-sky-600">{postsByPlatform.twitter}</p>
              <p className="text-sm text-gray-600 mt-1">Twitter</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Campaigns */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Active Campaigns</CardTitle>
            <Badge variant="success">{activeCampaigns.length} active</Badge>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {activeCampaigns.slice(0, 5).map((campaign) => (
                <div key={campaign.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{campaign.name}</p>
                      <p className="text-sm text-gray-500">
                        {campaign.campaign_type} • {campaign.goal_type}
                      </p>
                    </div>
                    <div className="text-right">
                      {campaign.goal_target && (
                        <div className="text-sm">
                          <span className="font-medium">
                            {campaign.goal_current || 0}
                          </span>
                          <span className="text-gray-400">
                            /{campaign.goal_target}
                          </span>
                        </div>
                      )}
                      <Badge variant={getStatusVariant(campaign.status)}>
                        {campaign.status}
                      </Badge>
                    </div>
                  </div>
                  {campaign.goal_target && (
                    <div className="mt-2">
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{
                            width: `${Math.min(
                              100,
                              ((campaign.goal_current || 0) / campaign.goal_target) * 100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {activeCampaigns.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <Megaphone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No active campaigns</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Content */}
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle>Recent Content</CardTitle>
            <Badge variant="info">{contentList.length} total</Badge>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100">
              {contentList.slice(0, 5).map((item) => (
                <div key={item.id} className="p-4 flex items-center gap-4">
                  {item.featured_image ? (
                    <img
                      src={item.featured_image}
                      alt={item.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{item.title}</p>
                    <p className="text-sm text-gray-500">
                      {item.content_type.replace('_', ' ')} •{' '}
                      {item.pillar?.replace('_', ' ') || 'No pillar'}
                    </p>
                  </div>
                  <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
                </div>
              ))}
              {contentList.length === 0 && (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No content yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Social Posts */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Upcoming Social Posts</CardTitle>
          <Badge variant="info">{scheduledPostsList.length} scheduled</Badge>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-gray-100">
            {scheduledPostsList.slice(0, 8).map((post) => (
              <div key={post.id} className="p-4 flex items-center gap-4">
                <div
                  className={`p-2 rounded-lg ${
                    post.platform === 'linkedin'
                      ? 'bg-blue-100 text-blue-600'
                      : post.platform === 'facebook'
                      ? 'bg-indigo-100 text-indigo-600'
                      : post.platform === 'instagram'
                      ? 'bg-pink-100 text-pink-600'
                      : 'bg-sky-100 text-sky-600'
                  }`}
                >
                  <Share2 className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 line-clamp-1">{post.post_text}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="default">{post.platform}</Badge>
                    <span className="text-sm text-gray-500">
                      {post.scheduled_for
                        ? format(new Date(post.scheduled_for), 'MMM d, h:mm a')
                        : 'Not scheduled'}
                    </span>
                  </div>
                </div>
                <Badge variant={getStatusVariant(post.status)}>{post.status}</Badge>
              </div>
            ))}
            {scheduledPostsList.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No scheduled posts</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
